import { FLAG_REGISTRY_DATA } from './flagRegistry.data.mjs'

export type DeployWorkflowId =
  | 'dev'
  | 'dev_mainnet'
  | 'dev_mainnet_wlth'
  | 'dev_wlth'
  | 'preprod'
  | 'preprod_wlth'
  | 'prod'
  | 'prod_wlth'
  | 'stage'
  | 'stage_wlth'

export type GardenerSeat = 'iain' | 'amer' | 'case'

export type FlagKind = 'boolean' | 'betaOnlyStrapi'

export type FlagDef = {
  kind: FlagKind
  env: `NEXT_PUBLIC_${string}`
  default: boolean
  owner: GardenerSeat
  allowedOn: readonly DeployWorkflowId[]
  retired?: boolean
}

export const FLAG_REGISTRY = FLAG_REGISTRY_DATA as Record<string, FlagDef>

export type FlagKey = keyof typeof FLAG_REGISTRY

/**
 * Next inlines NEXT_PUBLIC_* only for a static member access
 * (`process.env.NEXT_PUBLIC_BETA_ONLY`). A dynamic `process.env[name]`
 * read is empty in the browser, so every flag fell back to its registry
 * default. Beta-only then stayed on and hid the live slice funds.
 */
function readInlinedPublicEnv(envName: string): string | undefined {
  switch (envName) {
    case 'NEXT_PUBLIC_MARKETPLACE_ENABLED':
      return process.env.NEXT_PUBLIC_MARKETPLACE_ENABLED
    case 'NEXT_PUBLIC_MARKETPLACE_BIDS_ENABLED':
      return process.env.NEXT_PUBLIC_MARKETPLACE_BIDS_ENABLED
    case 'NEXT_PUBLIC_REWARDS_ENABLED':
      return process.env.NEXT_PUBLIC_REWARDS_ENABLED
    case 'NEXT_PUBLIC_BETA_ONLY':
      return process.env.NEXT_PUBLIC_BETA_ONLY
    case 'NEXT_PUBLIC_FUND_WALLET_PREVIEW_ENABLED':
      return process.env.NEXT_PUBLIC_FUND_WALLET_PREVIEW_ENABLED
    default:
      return undefined
  }
}

function readFlagEnv(envName: string): string | undefined {
  const value = readInlinedPublicEnv(envName)
  if (value === undefined) {
    return undefined
  }
  const trimmed = value.trim().toLowerCase()
  if (trimmed === '') {
    return undefined
  }
  return trimmed
}

function parseBooleanEnv(raw: string | undefined, defaultValue: boolean): boolean {
  if (raw === undefined) {
    return defaultValue
  }
  return raw === 'true'
}

/** Strapi fund queries use "beta only" filtering when this returns true. */
export function betaOnlyStrapiFilterEnabled(): boolean {
  const def = FLAG_REGISTRY.betaOnlyStrapi
  const raw = readFlagEnv(def.env)
  if (raw === undefined) {
    return def.default
  }
  if (raw === 'false' || raw === '0') {
    return false
  }
  if (raw === 'true' || raw === '1') {
    return true
  }
  return def.default
}

export function isOn(key: FlagKey): boolean {
  const def = FLAG_REGISTRY[key]
  if (def.retired) {
    return true
  }
  if (def.kind === 'betaOnlyStrapi') {
    return betaOnlyStrapiFilterEnabled()
  }
  return parseBooleanEnv(readFlagEnv(def.env), def.default)
}
